/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, {
  useState, useEffect, useRef,
} from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  Alert,
  Checkbox,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import API from 'utils/api';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { checkToken } from 'api/utils';
import { ESTATUS_ACTIVO, ESTATUS_AUTORIZADO } from 'utils/constants';
import { getConfiguracionExternaDescuentosDetalle } from 'api/ConfiguracionCargaExternaDescuentosDetalle';
import { getEmpleadosOptimized } from 'api/estructuraOrganizacional/empleados';
import getProcesoEnCursoTerminado from 'api/catalogos/ProcesoEnCursoTerminado';
import {
  deleteExternaDescuentoDetalle,
  eliminarExternaDescuentosDetalleBulk,
  getExternaDescuentosDetalle,
  getExternaDescuentosDetalleIds,
  patchExternaDescuentoDetalle,
  postExternaDescuentoDetalle,
} from 'api/catalogos/CargaExternaDescuentosDetalle';
import currencyFormatter from 'utils/currencyFormatter';

const DESCRIPCION_PROCESO_ELIMINAR_EMPLEADOS = 'Eliminar Empleados Carga Externa';

export const CargaExternaDescuentoDetallePermission = {
  permissionModel: 'descuentocargaexternaarchivoempleado',
  hasAllViewPermissions: true,
};

const CargaExternaDescuentoDetalle = ({
  CargaExternaDescuento,
  permission,
  registrosCargados = [],
  showParentForm,
  hideParentForm: _hideParentForm,
  pollingEnCurso = false, //  así se llama el prop
  usarRegistrosLocales = false, // nuevo prop para usar registros locales
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open2, setVisible2] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [], count: 0 });
  const [todosLosRegistros, setTodosLosRegistros] = useState([]);
  const pageSize = 10;
  const [pageActual, setPageActual] = useState(1);
  const [configuracionDescuentos, setConfiguracionDescuentos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [mostrarAlertaLocal, setMostrarAlertaLocal] = useState(false);
  const [isPollingDelete, setIsPollingDelete] = useState(false);
  const [progresoDelete, setProgresoDelete] = useState({ en_curso: 0, total: 0 });
  const pollingDeleteRef = useRef(null);
  const lastQueryRef = useRef(null);

  const isEditing = !!CargaExternaDescuento?.id;

  const formInstances = [form];

  // Solo se permite agregar/editar/eliminar filas de empleado cuando el archivo ya
  // existe, no se está mostrando una carga de Excel aún no guardada, no hay un
  // proceso asíncrono en curso y el archivo sigue en estatus Activo.
  const puedeEditar = isEditing
    && !usarRegistrosLocales
    && !pollingEnCurso
    && !isPollingDelete
    && CargaExternaDescuento?.estados_globales === ESTATUS_ACTIVO;

  const fetchData = async () => {
    setLoading(true);
    const _data = await getExternaDescuentosDetalle({ descuento_carga_externa_archivo: CargaExternaDescuento?.id });
    setData(formatReceived(_data));
    setLoading(false);
  };

  const fetchPaginated = async (page = 1, q = null) => {
    const baseURI = 'nominas/descuentos-carga-externa-archivo-empleado/';
    try {
      setLoading(true);

      const cleanPage = Number(String(page).replace(/\D/g, '')); // solo dígitos
      const response = await API.get(baseURI, {
        params: {
          page: cleanPage,
          descuento_carga_externa_archivo: CargaExternaDescuento?.id,
          ...q,
        },
      });

      const { results } = response.data;

      // Solo se limpia la selección cuando cambia el filtro/búsqueda (el subconjunto
      // de registros visible cambia), NO al navegar de página: "seleccionar todos"
      // debe sobrevivir el recorrido por las páginas del archivo.
      const qKey = JSON.stringify(q || {});
      if (lastQueryRef.current !== null && lastQueryRef.current !== qKey) {
        setSelectedRowKeys([]);
        setSelectedRows([]);
      }
      lastQueryRef.current = qKey;
      setPageActual(cleanPage);
      setData({ ...response.data, results });
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const paginarLocalmente = (registros, page) => {
    const desde = (page - 1) * pageSize;
    const hasta = desde + pageSize;
    const currentPageData = registros.slice(desde, hasta);

    setData({ results: currentPageData, count: registros.length });
    setPageActual(page);
  };

  useEffect(() => {
    let mounted = true;

    const fetchAll = async () => {
      if (usarRegistrosLocales) return; // ⬅️ no sobrescribas si hay carga local

      setLoading(true);
      await checkToken();

      const _data = await getExternaDescuentosDetalle({ descuento_carga_externa_archivo: CargaExternaDescuento?.id });
      const { results } = _data;

      if (mounted) {
        setData({ ..._data, results });
        setLoading(false);
      }
    };

    if (isEditing && CargaExternaDescuento) {
      fetchAll();
    }

    return () => { mounted = false; };
  }, [CargaExternaDescuento, isEditing, usarRegistrosLocales]);

  // Catálogo de conceptos relacionados (Descuento Clave y Concepto) configurados
  // para el concepto de carga externa del archivo padre.
  useEffect(() => {
    let mounted = true;

    const fetchConfiguracionDescuentos = async () => {
      if (!CargaExternaDescuento?.descuento_carga_externa) {
        if (mounted) setConfiguracionDescuentos([]);
        return;
      }
      const _data = await getConfiguracionExternaDescuentosDetalle({
        descuento_carga_externa: CargaExternaDescuento.descuento_carga_externa,
        estados_globales: ESTATUS_AUTORIZADO,
      });
      if (mounted) setConfiguracionDescuentos(_data?.results || []);
    };

    fetchConfiguracionDescuentos();

    return () => { mounted = false; };
  }, [CargaExternaDescuento?.descuento_carga_externa]);

  // Catálogo de empleados para el selector "Número de Empleado" del modal
  // Agregar/Editar — se carga una sola vez.
  useEffect(() => {
    let mounted = true;

    const fetchEmpleados = async () => {
      const _empleados = await getEmpleadosOptimized();
      if (mounted) setEmpleados(_empleados || []);
    };

    fetchEmpleados();

    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!pollingEnCurso && usarRegistrosLocales) {
      if (registrosCargados?.length > 0) {
        setTodosLosRegistros(registrosCargados);
        paginarLocalmente(registrosCargados, 1);
        setMostrarAlertaLocal(true);
      } else {
        setTodosLosRegistros([]);
        paginarLocalmente([], 1);
        setMostrarAlertaLocal(false);
      }
    }
  }, [registrosCargados, pollingEnCurso, usarRegistrosLocales]);

  useEffect(() => {
    if (!pollingEnCurso || !CargaExternaDescuento?.id) {
      return () => {}; // retorna función vacía si no hay polling
    }

    const interval = setInterval(() => {
      fetchData();
    }, 5000);

    return () => clearInterval(interval); // función de limpieza cuando se desmonta o cambia
  }, [pollingEnCurso, CargaExternaDescuento?.id]);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setVisible2(false);
    showParentForm();
    form.resetFields();
  };

  // Polling del borrado masivo (vía ProcesoEnCursoTerminado, mismo patrón que
  // Autorizar/Cancelar de la carga): consulta cada 2s hasta que `procesando` sea
  // false, actualizando la barra de progreso mostrada en el modal.
  useEffect(() => {
    if (!isPollingDelete) return undefined;

    let intentos = 0;
    const maxIntentos = 340; // máx ~11 min

    pollingDeleteRef.current = setInterval(async () => {
      try {
        intentos += 1;
        const lista = await getProcesoEnCursoTerminado({ descripcion: DESCRIPCION_PROCESO_ELIMINAR_EMPLEADOS });
        const proceso = lista[0];

        if (proceso) {
          setProgresoDelete({
            en_curso: proceso.registro_en_curso || 0,
            total: proceso.total_registros || 0,
          });
        }

        const termino = proceso && proceso.procesando === false;

        if (termino || intentos >= maxIntentos) {
          clearInterval(pollingDeleteRef.current);
          pollingDeleteRef.current = null;
          setIsPollingDelete(false);
          setProgresoDelete({ en_curso: 0, total: 0 });
          onCancel();
          await fetchData();
        }
      } catch (_err) {
        // No se interrumpe el polling: el siguiente tick reintenta. Si el
        // error es por sesión expirada, utils/api.js ya avisó y cerró
        // sesión; aquí solo dejamos rastro para diagnóstico.
        // eslint-disable-next-line no-console
        console.error('[polling borrado masivo carga externa] error al consultar estado', _err);
      }
    }, 2000);

    return () => {
      if (pollingDeleteRef.current) {
        clearInterval(pollingDeleteRef.current);
        pollingDeleteRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPollingDelete]);

  const onClickEdit = (responseData = null) => {
    const [key] = selectedRowKeys;
    const match = responseData || data?.results?.find((item) => item.id === key);
    if (!match) return;

    form.resetFields();
    form.setFieldsValue(formatReceived(match));
    setVisible2(true);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();

      const [key] = selectedRowKeys;

      const values = {
        ...form.getFieldsValue(),
        // Liga la fila de empleado al archivo padre
        descuento_carga_externa_archivo: CargaExternaDescuento?.id,
      };

      const saved = await (
        key
          ? patchExternaDescuentoDetalle(key, values)
          : postExternaDescuentoDetalle(values)
      );

      if (saved) {
        onCancel();
        await fetchData();
      }

      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  // Borrado de un único registro (uso normal, selección simple): directo, sin polling.
  const deleteItemUnico = async (key) => {
    try {
      setLoading(true);
      const deleted = await deleteExternaDescuentoDetalle(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  // Borrado masivo (selección múltiple o "seleccionar todos"): se delega a una tarea
  // de Celery que reporta avance vía ProcesoEnCursoTerminado, y el frontend hace
  // polling con una barra de progreso — igual patrón que Autorizar/Cancelar de la
  // carga — en vez de bloquear el navegador con cientos/miles de peticiones DELETE.
  const deleteItemMasivo = async () => {
    try {
      setVisibleAlert(false);
      const resultado = await eliminarExternaDescuentosDetalleBulk(selectedRowKeys);
      if (resultado?.descripcion) {
        setProgresoDelete({ en_curso: 0, total: selectedRowKeys.length });
        setIsPollingDelete(true);
      }
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    if (!selectedRowKeys.length) return;
    const [key] = selectedRowKeys;
    if (selectedRowKeys.length === 1) {
      await deleteItemUnico(key);
      return;
    }
    await deleteItemMasivo();
  };

  const handleOnRowClick = (record) => {
    if (selectedRowKeys.includes(record.id)) {
      setSelectedRowKeys((prev) => prev.filter((k) => k !== record.id));
      setSelectedRows((prev) => prev.filter((row) => row.id !== record.id));
    } else {
      setSelectedRowKeys((prev) => [...prev, record.id]);
      setSelectedRows((prev) => [...prev, record]);
    }
  };

  // Total real de registros del archivo (todas las páginas), no solo los ya renderizados.
  const totalRegistrosArchivo = usarRegistrosLocales
    ? todosLosRegistros.length
    : (data?.count || 0);

  // "Seleccionar todos" debe cubrir TODO el archivo, no solo la página visible: el
  // paginador del backend (CustomPageNumberPagination) regresa el listado completo sin
  // paginar cuando no se envía el parámetro `page`, así que se pide de nuevo bajo demanda.
  const handleSelectAllChange = async (checked) => {
    if (!checked) {
      setSelectedRowKeys([]);
      setSelectedRows([]);
      return;
    }
    if (usarRegistrosLocales) {
      setSelectedRowKeys(todosLosRegistros.map((row) => row.id));
      setSelectedRows(todosLosRegistros);
      return;
    }
    setLoading(true);
    try {
      // Endpoint ligero (solo ids, sin serializar cada fila completa): para un archivo
      // de miles de registros, traer todo vía el listado normal tarda decenas de
      // segundos y pesa varios MB; esto resuelve lo mismo en una sola consulta rápida.
      const ids = await getExternaDescuentosDetalleIds({
        descuento_carga_externa_archivo: CargaExternaDescuento?.id,
      });
      setSelectedRowKeys(ids || []);
      setSelectedRows([]);
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'checkbox',
    columnTitle: (
      <Checkbox
        checked={selectedRowKeys.length > 0 && selectedRowKeys.length === totalRegistrosArchivo}
        indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < totalRegistrosArchivo}
        onChange={(e) => handleSelectAllChange(e.target.checked)}
      />
    ),
    onSelect: (record, selected) => {
      if (selected) {
        setSelectedRowKeys((prev) => [...prev, record.id]);
        setSelectedRows((prev) => [...prev, record]);
      } else {
        setSelectedRowKeys((prev) => prev.filter((k) => k !== record.id));
        setSelectedRows((prev) => prev.filter((row) => row.id !== record.id));
      }
    },
    onChange: (keys, rows) => {
      setSelectedRowKeys(keys);
      setSelectedRows(rows);
    },
  };

  const onClickAdd = () => {
    setSelectedRowKeys([]);
    form.resetFields();
    setVisible2(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Número de Empleado',
      dataIndex: 'numero_de_empleado',
      key: 'numero_de_empleado',
      width: 100,
    },

    {
      titleText: 'Nombre del Empleado',
      dataIndex: 'nombre_del_empleado',
      key: 'nombre_del_empleado',
      width: 400,
    },

    {
      titleText: 'Descripción del Concepto Externo',
      dataIndex: 'descripcion_del_concepto_externo',
      key: 'descripcion_del_concepto_externo',
      width: 300,
    },
    {
      titleText: 'Descuento Clave y Concepto',
      dataIndex: 'descuento_clave_y_concepto',
      key: 'descuento_clave_y_concepto',
      width: 300,
    },

    {
      titleText: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 300,
      render: (val) => currencyFormatter(val || 0),
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    importe: [
      requiredRule,
      {
        validator: async (_rule, value) => {
          if (value !== undefined && value !== null && value !== '' && Number(value) <= 0) {
            throw new Error('El importe debe ser mayor a cero');
          }
        },
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading || pollingEnCurso || isPollingDelete}>
        {mostrarAlertaLocal && (
        <Alert
          message={`Se cargaron ${registrosCargados.length} registros desde el archivo Excel.`}
          type="info"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
        )}
        <Table
          cols={columns}
          data={data?.results || []}
          rowSelection={rowSelection}
          pagination={false}
          paginationCallback={(page, q) => (usarRegistrosLocales
            ? paginarLocalmente(todosLosRegistros, page)
            : fetchPaginated(page, q))}
          count={data?.count || 0}
          handleOnRowClick={handleOnRowClick}
          controls={puedeEditar ? {
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          } : undefined}
          permission={permission}
          allowExportPDF={false}
        />

        {/* El modal siempre está presente, pero se muestra solo si open2 es true */}
        <Modal
          open={open2}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Empleado de Carga Externa de Descuentos`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
                      )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            name="general"
            onFinish={onFinish}
            scrollToFirstError
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="numero_de_empleado"
                label="Número de Empleado"
                rules={rules.required}
              >
                <Select
                  valueProp="numero_de_empleado"
                  labelProp="numero_y_nombre_completo"
                  dataSource={empleados}
                  allowClear
                  showSearch
                  placeholder="Seleccione un empleado"
                  onChange={(value) => {
                    const match = empleados.find((e) => e.numero_de_empleado === value);
                    const nombreCompleto = match
                      ? `${match.nombre} ${match.apellido_paterno} ${match.apellido_materno}`.trim()
                      : '';
                    form.setFieldValue('nombre_del_empleado', nombreCompleto);
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="nombre_del_empleado"
                label="Nombre del Empleado"
                rules={rules.required}
              >
                <Input disabled />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="descuento_carga_externa_descuento"
                label="Descuento Clave y Concepto"
              >
                <Select
                  keyProp="id"
                  dataSource={configuracionDescuentos}
                  render={(value) => `${value?.concepto_relacionado || ''}${value?.descuento_clave_y_concepto ? ` (${value.descuento_clave_y_concepto})` : ''}`}
                  allowClear
                  showSearch
                  placeholder="Seleccione un concepto relacionado"
                  onChange={(value) => {
                    const match = configuracionDescuentos.find((e) => e.id === value);
                    form.setFieldValue('descripcion_del_concepto_externo', match?.concepto_relacionado || '');
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="descripcion_del_concepto_externo"
                label="Descripción del Concepto Externo"
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="importe"
                label="Importe"
                rules={rules.importe}
              >
                <Input type="number" step="0.01" min="0.01" />
              </Form.Item>
            </Col>

            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>

          </Form>

        </Modal>

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={selectedRowKeys.length === 1 && selectedRows[0]
            ? `el registro del empleado ${selectedRows[0].nombre_del_empleado || ''}`
            : `${selectedRowKeys.length} registro(s) de empleado seleccionado(s)`}
          loading={loading}
        />

        {/* Modal de progreso animado para el borrado masivo */}
        <Modal
          title="Eliminando registros..."
          open={isPollingDelete}
          footer={null}
          closable={false}
          maskClosable={false}
          width={420}
        >
          {isPollingDelete && (
            <div style={{ padding: '8px 0' }}>
              {progresoDelete.total > 0 ? (
                <>
                  <p style={{ marginBottom: 6, color: '#595959' }}>
                    {`Eliminando ${progresoDelete.en_curso} de ${progresoDelete.total} registros...`}
                  </p>
                  <div
                    style={{
                      background: '#f0f0f0',
                      borderRadius: 4,
                      overflow: 'hidden',
                      height: 12,
                      marginBottom: 4,
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${Math.round((progresoDelete.en_curso / progresoDelete.total) * 100)}%`,
                        background: '#1890ff',
                        borderRadius: 4,
                        transition: 'width 0.5s ease',
                      }}
                    />
                  </div>
                  <p
                    style={{
                      textAlign: 'right', fontSize: 12, color: '#8c8c8c', margin: 0,
                    }}
                  >
                    {`${Math.round((progresoDelete.en_curso / progresoDelete.total) * 100)}%`}
                  </p>
                </>
              ) : (
                <>
                  <p style={{ marginBottom: 8, color: '#595959' }}>
                    Por favor espere...
                  </p>
                  <div
                    style={{
                      background: '#f0f0f0',
                      borderRadius: 4,
                      overflow: 'hidden',
                      height: 12,
                      position: 'relative',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        height: '100%',
                        width: '40%',
                        background: '#1890ff',
                        borderRadius: 4,
                        animation: 'indeterminate-delete 1.4s infinite ease-in-out',
                      }}
                    />
                  </div>
                  <style>
                    {'@keyframes indeterminate-delete { 0% { left: -40%; } 100% { left: 110%; } }'}
                  </style>
                </>
              )}
            </div>
          )}
        </Modal>
      </Spin>
    </Row>
  );
};

export default CargaExternaDescuentoDetalle;
